/**
 * Die Aktion aus einer Benachrichtigung ausführen.
 *
 * Der Service Worker hängt `?quittieren=<erinnerungsId>&aktion=…` an die Adresse
 * und öffnet die App. Hier wird daraus ein Haken — **über dieselbe Funktion, die
 * auch der Knopf in der Liste benutzt.** Zwei Wege zum selben Ergebnis wären zwei
 * Stellen, an denen dieselbe Regel stehen müsste.
 */

import { ESTIMATE_VIEW_PARAM } from '@kraftwerk/shared';
import { acknowledgeTrainingDay } from '../db/schedule.js';
import { db } from '../db/local.js';
import type { KraftwerkDatabase } from '../db/local.js';
import { acknowledgeIntake, skipIntake } from '../db/intakes.js';
import { acknowledgePrep } from '../db/prep.js';

export interface NotificationAction {
  reminderId: string;
  /**
   * Fehlt, wenn die Benachrichtigung nur angetippt wurde.
   *
   * **Auf iOS ist das der Normalfall:** Aktions-Knöpfe werden dort nicht
   * angezeigt (am Gerät geprüft, 14.08.2026 — iOS 18.7). Antippen ist der
   * einzige Weg, und dann wird bewusst NICHT automatisch quittiert: Wer
   * nachsieht, was ansteht, hat es damit noch nicht genommen.
   */
  action: 'genommen' | 'heute-nicht' | null;
}

export function readNotificationAction(search: string): NotificationAction | null {
  const params = new URLSearchParams(search);
  const reminderId = params.get('quittieren');
  if (!reminderId) return null;

  const raw = params.get('aktion');
  const action = raw === 'genommen' || raw === 'heute-nicht' ? raw : null;

  return { reminderId, action };
}

/**
 * Eine Mitteilung, die nur eine Ansicht öffnet (etwa der Wochenrückblick am
 * Sonntag) — ohne Erinnerung, auf die gewartet werden müsste.
 */
/**
 * `week`: Wochenrückblick, `location`: Standort im Haushalt (27.10.2026),
 * `estimate`: Foto-Schätzung fertig oder gescheitert (28.10.2026) — in den Tag
 * mit dem Platzhalter.
 */
export type NotificationView = 'week' | 'location' | 'estimate';

export function readNotificationView(search: string): NotificationView | null {
  const view = new URLSearchParams(search).get('ansicht');
  if (view === 'woche') return 'week';
  if (view === 'standort') return 'location';
  if (view === ESTIMATE_VIEW_PARAM) return 'estimate';
  return null;
}

/** Zu welcher Einnahme gehört die Erinnerung? */
export async function intakeForReminder(
  reminderId: string,
  database: KraftwerkDatabase = db,
): Promise<string | null> {
  const reminder = await database.reminders.get(reminderId);
  return typeof reminder?.refId === 'string' ? reminder.refId : null;
}

/** Wohin das Antippen führt: zur Einnahme oder zur Kachel „Für morgen". */
export type NotificationTarget =
  | { kind: 'intake'; id: string }
  | { kind: 'prep'; id: string }
  | { kind: 'training'; id: string }
  | { kind: 'task'; id: string }
  | { kind: 'meal'; id: string }
  | { kind: 'water'; id: string };

/**
 * Löst die Erinnerung zu ihrem Auslöser auf und setzt — falls gewählt — den Haken.
 *
 * Seit E6b gibt es zwei Arten. Eine Vorbereitungs-Erinnerung als Einnahme zu
 * behandeln, führte auf eine Seite mit „Genommen" für ein Supplement, das es
 * nicht gibt.
 *
 * `reminders` liegt lokal vor (nur lesend synchronisiert), `refId` zeigt auf die
 * Einnahme. Findet sich nichts — etwa weil noch nicht synchronisiert wurde —
 * passiert nichts; der Nutzer sieht die offene Zeile in der Liste und kann dort
 * tippen.
 */
export async function applyNotificationAction(
  action: NotificationAction,
  database: KraftwerkDatabase = db,
): Promise<NotificationTarget | null> {
  const reminder = await database.reminders.get(action.reminderId);
  const refId = typeof reminder?.refId === 'string' ? reminder.refId : null;
  if (!refId) return null;

  // „Losgehen um …": Antippen heißt gesehen — das quittiert, wie bei der
  // Abend-Erinnerung. Mehr gibt es nicht zu entscheiden.
  // Aufgabe: Antippen führt hin — erledigt wird sie mit dem Haken, nicht
  // durchs Ansehen.
  if (reminder?.kind === 'task') return { kind: 'task', id: refId };
  // Mahlzeit (16.10.2026): Antippen führt in den Tag — eingetragen wird dort,
  // nicht durchs Ansehen. Keine Einnahme: „genommen“ hätte hier nichts zu tun.
  if (reminder?.kind === 'meal') return { kind: 'meal', id: refId };
  // Wasser (18.10.2026): wie die Mahlzeit in den Tag — dort wird eingetragen.
  if (reminder?.kind === 'water') return { kind: 'water', id: refId };

  if (reminder?.kind === 'training') {
    await acknowledgeTrainingDay(refId, database);
    return { kind: 'training', id: refId };
  }

  if (reminder?.kind === 'prep') {
    if (action.action === 'genommen') await acknowledgePrep(refId, database);
    return { kind: 'prep', id: refId };
  }

  // Ohne gewählte Aktion wird nur hingeführt, nicht gehandelt.
  if (action.action === 'genommen') await acknowledgeIntake(refId, database);
  else if (action.action === 'heute-nicht') await skipIntake(refId, null, database);

  return { kind: 'intake', id: refId };
}

/** Entfernt die Parameter aus der Adresse, damit ein Neuladen nichts wiederholt. */
export function clearNotificationParams(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('quittieren');
  url.searchParams.delete('aktion');
  url.searchParams.delete('ansicht');
  window.history.replaceState({}, '', url.pathname + url.search);
}
